import {ChangeDetectionStrategy, Component, HostBinding, HostListener, booleanAttribute, forwardRef, input, model} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

/** Which side of the chip's contents the handle sits on. */
export type SwitchKnobPosition = 'start' | 'end';

/**
 * The app's on/off chip: a handle, and an optional icon and label beside it.
 *
 * The host is the whole control, so a click anywhere on it toggles — handle, icon, label, or the
 * padding between them. That is why there is no `<input>` and no `for`: the copies this replaces
 * each paired a hidden checkbox with labels, and every part that lacked a `for` was dead to the
 * pointer while looking clickable.
 *
 * Usable as `[(value)]` or through a form control; `TriStateSwitchComponent` is the variant for a
 * multi-edit form, where a third state says "leave this field alone".
 */
@Component({
  selector: 'app-switch',
  templateUrl: './switch.component.html',
  styleUrl: './switch.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => SwitchComponent),
  }],
})
export class SwitchComponent implements ControlValueAccessor {
  /** The state, for a caller binding it directly; a form control writes to the same model. */
  readonly value = model(false);
  /** Text beside the handle. Mark it `i18n-label` at the call site. */
  readonly label = input('');
  /** A material-icons ligature ahead of the label, for a chip that names itself with a glyph. */
  readonly icon = input<string | null>(null);
  /**
   * The class the glyph is drawn with, so a caller can reach either icon font — `material-icons`
   * by default, `material-icons-outlined` for the variant, `material-symbols-outlined` for the
   * glyphs only that set has.
   */
  readonly iconClass = input('material-icons');
  /**
   * Where the handle sits. `start` — the default — reads as "switch, then what it controls";
   * `end` suits a chip whose icon is the subject, with the handle trailing it.
   */
  readonly knobPosition = input<SwitchKnobPosition>('start');
  readonly disabled = input(false, {transform: booleanAttribute});

  @HostBinding('class.on') get isOn(): boolean {
    return this.value();
  }

  @HostBinding('class.disabled') get isDisabled(): boolean {
    return this.disabled() || this.formDisabled;
  }

  /** Set by `setDisabledState`; ORed with the input above so either can block the toggle. */
  private formDisabled = false;
  private onChange: (value: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  @HostListener('click')
  protected toggle(): void {
    if (this.isDisabled) {
      return;
    }
    this.value.set(!this.value());
    this.onTouched();
    this.onChange(this.value());
  }

  writeValue(value: boolean | null): void {
    this.value.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
  }
}
